<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Perfil / Incidencias</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <PeriodSelector 
            v-if="studentId"
            :studentId="studentId"
            v-model="selectedPeriod"
            @period-changed="loadData"
          />

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="noDataAvailable" class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Sin información disponible
          </div>

          <div v-else-if="incidencias && incidencias.length > 0" class="mb-6">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold mb-2">Registro de Incidencias del Periodo {{ selectedPeriod }}:</h2>
              <span class="text-sm text-gray-600">
                {{ incidencias.length }} registro(s) encontrado(s)
              </span>
            </div>
            
            <!-- Vista móvil: tarjetas individuales -->
            <div class="grid grid-cols-1 gap-4 sm:hidden">
              <div 
                v-for="item in sortedIncidencias" 
                :key="item.Clave"
                class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 border-blue-500"
              >
                <div class="grid grid-cols-2 gap-y-2">
                  <div class="text-sm font-medium text-gray-500">Clave:</div>
                  <div class="text-sm">{{ item.Clave }}</div>

                  <div class="text-sm font-medium text-gray-500">Incidencia:</div>
                  <div class="text-sm">{{ item.Incidencia }}</div>

                  <div class="text-sm font-medium text-gray-500">Fecha:</div>
                  <div class="text-sm font-bold">{{ formatDate(item.Fecha) }}</div>
                  
                  <div class="text-sm font-medium text-gray-500">Comentario:</div>
                  <div class="text-sm">{{ item.Comentario }}</div>
                </div>
              </div>
            </div>
            
            <!-- Vista escritorio: tabla tradicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Clave</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Incidencia</th>
                    <th scope="col" class="py-3 pl-4 pr-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Fecha</th>
                    <th scope="col" class="py-3 pl-4 pr-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Comentario</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white text-sm font-medium text-gray-900">
                  <tr 
                    v-for="item in sortedIncidencias" 
                    :key="item.Clave"
                  >
                    <td class="whitespace-nowrap px-3 py-4 text-center text-sm">
                      {{ item.Clave }}
                    </td>
                    <td class="px-3 py-4 text-center text-sm">
                      {{ item.Incidencia }}
                    </td>
                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-center text-sm">
                      {{ formatDate(item.Fecha) }}
                    </td>
                    <td class="py-4 pl-4 pr-3 text-center text-sm">
                      {{ item.Comentario }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-else class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 hidden">
            No hay incidencias disponibles para mostrar.
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import PeriodSelector from '@/components/PeriodSelector.vue';
import { getIncidents } from "@/services/api";

export default {
  components: {
    SidebarMenu,
    PeriodSelector
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      incidencias: null,
      loading: false,
      error: null,
      studentId: null,
      noDataAvailable: false,
      selectedPeriod: '',
    };
  },
  computed: {
    // Ordenar las incidencias por fecha de más reciente a más antigua
    sortedIncidencias() {
      if (!this.incidencias) return [];
      
      return [...this.incidencias].sort((a, b) => {
        const dateA = new Date(a.Fecha);
        const dateB = new Date(b.Fecha);
        return dateB - dateA;
      });
    }
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    // Método para formatear fechas en formato dd/mm/yyyy
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];

          // Cargar datos con el ID extraído
          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    async loadData(periodId) {
      if (!periodId) return;
      try {
        this.loading = true;
        this.error = null;
        this.noDataAvailable = false;

        const response = await getIncidents({ idstudent: this.studentId, idperiod: periodId });
        
        // Verificar si la respuesta tiene datos
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          this.incidencias = response.data;
        } else {
          // Si api respondió correctamente pero no hay datos
          this.incidencias = [];
          this.noDataAvailable = true;
        }
        
        //this.loading = false;
      } catch (error) {
        this.loading = false;
        
        // 404 = URL existe pero no hay datos para este estudiante
        if (error.response && error.response.status === 404) {
          this.noDataAvailable = true;
          return;
        }
        
        // Cualquier otro error = problema de conexión/URL/servidor
        console.error("Error al obtener los datos:", error);
        this.error = "Error al cargar los datos. Por favor, intente nuevamente.";
      } finally {
        this.loading = false;
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');

        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));

        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>